import { UIKit } from "@/shared/ui";
/**
 * Виджет «Свойства» — правая панель редактора.
 *
 * Показывает параметры выделенного объекта (стена / проём / угол / прибор / светильник /
 * кабель) и позволяет задать их числом, а не мышью: для чертежа это основной способ ввода.
 * У выключателя здесь видно, какие светильники он зажигает, у светильника — какой
 * выключатель его включает: группа освещения читается из проводки, а не хранится.
 * Не хранит паспорт проекта и экспликацию: это отдельное меню верхней панели.
 */
import {
	cableLength,
	cornerAngle,
	cornerAngleMove,
	detectRooms,
	pipeLength,
	wallFaceLengths,
	wallLength,
} from "@/widgets/plan";
import { MaterialSwatch } from "@/widgets/catalog";
import {
	CatalogAttach,
	catalogSiblings,
	getCatalogItem,
	isDecor,
	isRug,
	clampItemSize,
	sectionOfItem,
	type CatalogItem,
} from "@/shared/catalog";
import { cableEndCatalogItem, lightsOfSwitch, switchesOfLight } from "@/widgets/electrical";
import { pipeEndCatalogItem } from "@/widgets/plumbing";
import {
	WaterMedium,
	acceptsMedium,
	defaultPipe,
	isSwitch,
	pipeItemById,
	waterMediumColor,
	waterMediumLabel,
} from "@/shared/catalog";
import {
	usePipesStore,
	useWallsStore,
	useFixturesStore,
	wallFixtureOwner,
	floorItemOwner,
	useFloorsStore,
} from "@/shared/store/layers";
import { useEditorStore } from "@/widgets/editor";
import { DEFAULT_WALL_MATERIAL, useProjectStore } from "@/shared/store";
import { remove } from "@/shared/store/actions";
import { formatArea, formatLength } from "@/shared/lib/geometry";
import { itemDimensions, resizeFloorItem, type Fixture, type FloorItem } from "@/shared/plan";
import {
	CATALOG,
	allowedFloorLayouts,
	floorLook,
	getFloorLayout,
	getMaterial,
} from "@/shared/catalog";
import { findFixture, findFloorItem } from "@/shared/plan";
import { ItemSizeFields } from "./ItemSizeFields";
import { RoomCeilingProperties } from "./RoomCeilingProperties";
import { ObjectComments } from "./ObjectComments";
import { CommentProperties } from "./CommentProperties";
import { VariantsStrip } from "@/widgets/place-fixture";

/** Предмет на полу лежит в мебели или в сантехнике — правим через владельца. */
const updateFloorItem = (id: string, patch: Partial<Omit<FloorItem, "id">>) =>
	floorItemOwner(id)?.getState().updateFloorItem(id, patch);

void [
	UIKit,
	cableLength,
	detectRooms,
	pipeLength,
	wallFaceLengths,
	wallLength,
	MaterialSwatch,
	CatalogAttach,
	catalogSiblings,
	getCatalogItem,
	isDecor,
	isRug,
	clampItemSize,
	sectionOfItem,
	cableEndCatalogItem,
	lightsOfSwitch,
	switchesOfLight,
	pipeEndCatalogItem,
	WaterMedium,
	acceptsMedium,
	defaultPipe,
	isSwitch,
	pipeItemById,
	waterMediumColor,
	waterMediumLabel,
	usePipesStore,
	useWallsStore,
	useFixturesStore,
	wallFixtureOwner,
	floorItemOwner,
	useFloorsStore,
	useEditorStore,
	DEFAULT_WALL_MATERIAL,
	useProjectStore,
	remove,
	formatArea,
	formatLength,
	itemDimensions,
	resizeFloorItem,
	CATALOG,
	allowedFloorLayouts,
	floorLook,
	getFloorLayout,
	getMaterial,
	findFixture,
	findFloorItem,
	ItemSizeFields,
	RoomCeilingProperties,
	ObjectComments,
	CommentProperties,
	VariantsStrip,
	updateFloorItem,
];

export type PropertiesPanelCatalogItem = CatalogItem;

export const NodeProperties = () => {
	const layers = useProjectStore((s) => s.project.layers);
	const graph = layers.walls;
	const selection = useEditorStore((s) => s.selection);
	const setSelection = useEditorStore((s) => s.setSelection);
	const activeCatalog = useEditorStore((s) => s.activeCatalog);
	const viewMode = useEditorStore((s) => s.viewMode);
	const updateWall = useWallsStore((s) => s.updateWall);
	const setWallLength = useWallsStore((s) => s.setWallLength);
	const updateOpening = useWallsStore((s) => s.updateOpening);
	const setRoomFloorMaterial = useFloorsStore((s) => s.setRoomFloorMaterial);
	const updatePipe = usePipesStore((s) => s.updatePipe);
	const removeRoomFloor = useFloorsStore((s) => s.removeRoomFloor);
	const moveNode = useWallsStore((s) => s.moveNode);
	const pushHistory = useProjectStore((s) => s.pushHistory);
	const recordEdit = useProjectStore((s) => s.recordEdit);

	const electricalFixtures = useFixturesStore((s) => s.items);
	const updateCeilingItem = useFixturesStore((s) => s.updateCeilingItem);
	const updateFixtureElectrical = useFixturesStore((s) => s.updateFixture);

	// Хелпер для updateFixture в зависимости от каталога
	const updateFixture = (id: string, patch: Partial<Fixture>) => {
		if (electricalFixtures[id]) {
			updateFixtureElectrical(id, patch);
		} else {
			wallFixtureOwner(id)?.getState().updateFixture(id, patch);
		}
	};

	const onDelete = () => {
		// Удаление кнопкой отменяется по Ctrl+Z, как и клавишей Delete
		pushHistory();
		remove(selection);
		setSelection(null);
	};

	const rooms = detectRooms(graph);
	void [
		activeCatalog,
		viewMode,
		updateWall,
		setWallLength,
		updateOpening,
		setRoomFloorMaterial,
		updatePipe,
		removeRoomFloor,
		moveNode,
		electricalFixtures,
		updateCeilingItem,
		updateFixtureElectrical,
		updateFixture,
		rooms,
		onDelete,
	];

	if (!selection) return null;
	// --- угол ----------------------------------------------------------------
	const node = graph.nodes[selection.id];
	if (!node) return null;
	const angle = cornerAngle(graph, node.id);
	const wallsAtNode = Object.values(graph.walls).filter(
		(wall) => wall.a === node.id || wall.b === node.id,
	).length;
	return (
		<>
			<UIKit.Text className="text-sm font-semibold tracking-label text-ink uppercase">
				Угол
			</UIKit.Text>
			{/* Вместо координат X и Y — угол между стенами: «90°» понятно всем, координаты — нет */}
			{angle === null ? (
				<UIKit.Text className="text-xs text-ink">
					Угол задаётся там, где сходятся ровно две стены. Здесь их {wallsAtNode} — двигайте угол
					мышью или меняйте длины стен.
				</UIKit.Text>
			) : (
				<>
					<UIKit.NumberField
						label="Угол между стенами"
						unit="°"
						value={angle}
						min={1}
						max={179}
						step={1}
						onChange={(degrees) => {
							const move = cornerAngleMove(graph, node.id, degrees);
							if (!move) return;
							recordEdit(() => moveNode(move.nodeId, move.x, move.y));
						}}
					/>
					<UIKit.Text className="text-xs text-ink">
						Поворачивается одна из двух стен вокруг угла, длина её не меняется. У прямоугольной
						комнаты — 90°.
					</UIKit.Text>
				</>
			)}
			<UIKit.Button
				type="button"
				className="mt-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-sm text-danger hover:bg-red-50"
				onClick={onDelete}
			>
				Удалить угол и стены
			</UIKit.Button>
		</>
	);
};
